import { inject, Injectable, TemplateRef, ViewContainerRef } from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { BehaviorSubject, take } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { getMobileBottomNavOffset } from "@shared/utils/get-mobile-bottom-nav-offset";

@Injectable()
export class BottomDrawerService {
  private overlayRef: OverlayRef | null = null;
  private _isOpen$ = new BehaviorSubject<boolean>(false);

  private readonly overlay = inject(Overlay);

  readonly isOpen$ = this._isOpen$.asObservable();
  readonly isOpen = toSignal(this.isOpen$, { initialValue: false });

  open(templateRef: TemplateRef<unknown>, viewContainerRef: ViewContainerRef, height: string = "100%"): void {
    if (this.overlayRef) {
      this.close();
    }

    const navBarHeight = getMobileBottomNavOffset() || 80;
    const bottomOffset = `${navBarHeight}px`;

    const positionStrategy = this.overlay.position().global().bottom(bottomOffset).left().right();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      height: `calc(${height} - ${bottomOffset}`,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      panelClass: "bottom-drawer-overlay-panel",
    });

    const portal = new TemplatePortal(templateRef, viewContainerRef);
    this.overlayRef.attach(portal);
    this._isOpen$.next(true);

    this.overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        this.close();
      });
  }

  close(): void {
    if (this.overlayRef) {
      this._isOpen$.next(false);
      this.overlayRef?.dispose();
      this.overlayRef = null;
    }
  }
}
